import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Phone, Mail, MapPin, Clock, AlertTriangle, Send, Loader2, CheckCircle } from 'lucide-react';
import { BUSINESS } from '@/lib/businessData';
import Seo from '@/components/Seo';

export default function Contact() {
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Build a mailto link as a lightweight contact fallback
    const subject = `Service Inquiry from ${form.name}`;
    const body = `Name: ${form.name}\nPhone: ${form.phone}\n\nMessage: ${form.message}`;
    window.location.href = `mailto:${BUSINESS.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 500);
  };

  const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(BUSINESS.address)}&output=embed`;

  return (
    <>
      <Seo
        title="Contact Mike's Garage Door Repair | 24/7 Emergency Cypress TX"
        description="Contact Mike's Garage Door Repair in Cypress, TX. Call our 24/7 emergency hotline, email us, or send a message — a real person answers the phone every time."
        path="/contact"
      />
      <Navbar />
      <main className="pt-16">
        {/* Emergency banner */}
        <div className="bg-destructive text-destructive-foreground">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-center gap-2 text-center">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <span className="font-heading font-bold text-xs uppercase tracking-wider">
              Garage Door Emergency? Call Now — {BUSINESS.hours}
            </span>
          </div>
        </div>

        {/* Hero */}
        <section className="bg-background border-b border-border tech-grid">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
            <div className="max-w-3xl">
              <div className="font-mono text-xs text-primary uppercase tracking-wider stencil mb-4">
                / Contact Mike's Garage Door Repair
              </div>
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-foreground text-balance leading-[0.95]">
                Get In Touch. <span className="text-primary">We Answer.</span>
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
                Call us any time — a real person answers the phone {BUSINESS.hours}. For non-urgent inquiries,
                send us a message and we'll get back to you within the hour during business hours.
              </p>
            </div>
          </div>
        </section>

        {/* Contact info + Map */}
        <section className="bg-background py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Contact details */}
              <div>
                <div className="font-mono text-xs text-primary uppercase tracking-wider stencil mb-3">/ Contact Details</div>
                <h2 className="font-heading font-black text-3xl text-foreground mb-8">Reach Mike's Team</h2>
                <div className="space-y-4">
                  {/* Emergency phone */}
                  <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-start gap-4 bg-primary text-background p-5 hover:amber-glow transition-all">
                    <Phone className="w-6 h-6 shrink-0" />
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-wider opacity-80">24/7 Emergency Hotline</div>
                      <div className="font-heading font-black text-xl">{BUSINESS.phone}</div>
                      <div className="font-mono text-xs opacity-80 mt-1">Tap to call — answered by a real person</div>
                    </div>
                  </a>
                  {/* Email */}
                  <a href={`mailto:${BUSINESS.email}`} className="flex items-start gap-4 bg-card border border-border p-5 hover:border-primary/50 transition-all">
                    <Mail className="w-6 h-6 text-primary shrink-0" />
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Email</div>
                      <div className="font-heading font-bold text-sm text-foreground break-all">{BUSINESS.email}</div>
                    </div>
                  </a>
                  {/* Address */}
                  <div className="flex items-start gap-4 bg-card border border-border p-5">
                    <MapPin className="w-6 h-6 text-primary shrink-0" />
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Shop Location</div>
                      <div className="font-heading font-bold text-sm text-foreground">{BUSINESS.address}</div>
                    </div>
                  </div>
                  {/* Hours */}
                  <div className="flex items-start gap-4 bg-card border border-border p-5">
                    <Clock className="w-6 h-6 text-primary shrink-0" />
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Business Hours</div>
                      <div className="font-heading font-bold text-sm text-foreground">{BUSINESS.hours}</div>
                      <div className="font-mono text-xs text-muted-foreground mt-1">Emergency dispatch available all day, every day</div>
                    </div>
                  </div>
                </div>

                {/* License */}
                <div className="mt-6 bg-card border border-border p-5">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Licensed & Insured</div>
                  <div className="font-heading font-bold text-sm text-foreground">{BUSINESS.license}</div>
                </div>
              </div>

              {/* Map */}
              <div>
                <div className="font-mono text-xs text-primary uppercase tracking-wider stencil mb-3">/ Find Us</div>
                <h2 className="font-heading font-black text-3xl text-foreground mb-8">Cypress Garage Door Shop</h2>
                <div className="border-2 border-border overflow-hidden">
                  <iframe
                    title="Mike's Garage Door Repair location map"
                    src={mapSrc}
                    className="w-full h-80 lg:h-96"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <p className="mt-3 font-mono text-xs text-muted-foreground">
                  Located on Cypress Rosehill Rd (FM 529) — central to all Cypress neighborhoods.
                  Average 30-minute response time for emergency calls.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Message form */}
        <section className="bg-card border-y border-border py-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <div className="font-mono text-xs text-primary uppercase tracking-wider stencil mb-3">/ Send a Message</div>
              <h2 className="font-heading font-black text-3xl text-foreground text-balance">
                Non-Urgent Inquiry? Drop Us a Line.
              </h2>
              <p className="mt-3 text-muted-foreground">
                For emergencies, always call {BUSINESS.phone}. For everything else, fill this out and we'll respond within the hour.
              </p>
            </div>

            {submitted ? (
              <div className="bg-background border border-primary/30 p-8 text-center">
                <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg text-foreground mb-2">Message Ready!</h3>
                <p className="text-sm text-muted-foreground">
                  Your email client should have opened. If not, call us at{' '}
                  <a href={`tel:${BUSINESS.phoneRaw}`} className="text-primary hover:underline">{BUSINESS.phone}</a>.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Your Name</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-background border border-border px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none transition-colors"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Phone Number</label>
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full bg-background border border-border px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none transition-colors"
                    placeholder="(281) 555-0123"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">How Can We Help?</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-background border border-border px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none transition-colors resize-none"
                    placeholder="Describe your garage door issue..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-background px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:amber-glow transition-all disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary text-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading font-black text-2xl sm:text-3xl">Need Service Right Now?</h2>
              <p className="font-mono text-sm opacity-80 mt-1">Call {BUSINESS.hours} — we dispatch immediately.</p>
            </div>
            <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-center gap-2 bg-background text-primary px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:opacity-90 transition-opacity">
              <Phone className="w-5 h-5" />
              {BUSINESS.phone}
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}